import { useSelector } from 'react-redux';
import { AppState } from '../store/store';
import moment from 'moment';

const parseDateWithoutTimezoneConversion = (date: Date | string) => {
  if (!date) {
    return null;
  }

  if (date instanceof Date) {
    return moment(date);
  }

  const rawDate = `${date}`.trim();
  if (!rawDate) {
    return null;
  }

  const matchedDate = rawDate.match(/^(\d{4}-\d{2}-\d{2})/);
  if (matchedDate) {
    const parsedDate = moment(matchedDate[1], 'YYYY-MM-DD', true);
    return parsedDate.isValid() ? parsedDate : null;
  }

  const parsedDateWithZone = moment.parseZone(rawDate, moment.ISO_8601, true);
  if (parsedDateWithZone.isValid()) {
    return parsedDateWithZone;
  }

  const parsedDate = moment(rawDate);
  return parsedDate.isValid() ? parsedDate : null;
};

export const useDateTimeFormatter: ((showTime?: boolean) => (date: Date) => string) = (showTime = false) => {
  const locale = useSelector<AppState, string>(state => state.settings.locale);
  const timezone = useSelector<AppState, string | undefined>(state => state.settings.timezone);
  const format = ((locale.toLowerCase() === 'iso8601' || locale.toLocaleLowerCase() === 'x-iso8601'))
    ? (showTime ? 'YYYY/MM/DD HH:mm' : 'YYYY/MM/DD')
    : (showTime
      ? `${moment().locale(locale).localeData().longDateFormat('L')} ${moment().locale(locale).localeData().longDateFormat('LT')}`
      : moment().locale(locale).localeData().longDateFormat('L'));
  return (date: Date) => timezone ? moment.tz(date, timezone).format(`${format} z`) : moment.utc(date).format(`${format} z`);
};

export const useDateOnlyFormatterWithoutTimezoneConversion: (() => (date: Date | string) => string) = () => {
  const locale = useSelector<AppState, string>(state => state.settings.locale);
  const format = locale.toLowerCase() === 'iso8601' || locale.toLocaleLowerCase() === 'x-iso8601'
    ? 'YYYY/MM/DD'
    : moment().locale(locale).localeData().longDateFormat('L');

  return (date: Date | string) => {
    const parsedDate = parseDateWithoutTimezoneConversion(date);
    return parsedDate ? parsedDate.locale(locale).format(format) : '';
  };
};
